import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HowItWorks.css';

const STEPS = [
  {
    num: '01',
    title: 'Submit Your Request',
    body: 'Fill out the quote form with your origin, destination, cargo type, and timeline. Takes 90 seconds — no account required.',
  },
  {
    num: '02',
    title: 'We Audit Your Lanes',
    body: 'Our team analyzes your routes, volume, and delivery windows. We identify the fastest, most cost-effective carrier mix for your specific needs.',
  },
  {
    num: '03',
    title: 'Receive Your Custom Plan',
    body: 'You get a clear proposal: rates, transit times, liability coverage, and a dedicated point of contact. No hidden fees, no vague estimates.',
  },
  {
    num: '04',
    title: 'We Execute & Report',
    body: 'Once approved, we dispatch, track, and proactively update you at every milestone. You have real-time visibility and one number to call if anything changes.',
  },
];

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export const HowItWorks = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const targets = sectionRef.current?.querySelectorAll<HTMLElement>('.rn-reveal');
    if (!targets) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); observer.unobserve(e.target); }
      }),
      { threshold: 0.1 }
    );
    targets.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="rn-hiw" ref={sectionRef}>
      <div className="rn-hiw__inner">

        <div className="rn-hiw__header rn-reveal">
          <span className="cs-topper">How It Works</span>
          <h2 className="rn-hiw__heading">From First Contact to Final Delivery</h2>
          <p className="rn-hiw__sub">
            A transparent, four-step process built to remove uncertainty — so you
            know exactly what happens after you reach out.
          </p>
        </div>

        <ol className="rn-hiw__steps">
          {STEPS.map(({ num, title, body }, i) => (
            <li key={num} className={`rn-hiw__step rn-reveal rn-reveal-delay-${i % 3 + 1}`}>
              <div className="rn-hiw__step-num">{num}</div>
              <div className="rn-hiw__step-body">
                <h3 className="rn-hiw__step-title">{title}</h3>
                <p className="rn-hiw__step-text">{body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="rn-hiw__cta rn-reveal">
          <div className="rn-hiw__trust">
            {['No commitment required', 'Response within 24 hours', 'Free lane audit'].map(t => (
              <span key={t} className="rn-hiw__trust-item">
                <CheckIcon />{t}
              </span>
            ))}
          </div>
          <Link to="/quote" className="cs-button-solid">Start Your Quote</Link>
        </div>

      </div>
    </section>
  );
};
